/**
 * The typed time field's two directions: what someone types into it, read as
 * the 24-hour "HH:mm" every caller stores, and that value drawn back as
 * "hh:mm AM" for reading.
 */

/**
 * Read a typed time as "HH:mm", '' for an empty field, or null for text that
 * is not a time yet. Forgiving on purpose: "930", "9:30", "930p", "2pm" and
 * "14:30" are all times. Without an a or p the hour is on the 24-hour clock,
 * so "2:30" is 02:30 and "14:30" is 14:30.
 */
export function parseTypedTime(text: string): string | null {
  const typed = text.toLowerCase().replace(/[\s.]/g, '');
  if (typed === '') return '';

  const match = /^(\d{1,2}):(\d{2})([ap])?m?$|^(\d{1,4})([ap])?m?$/.exec(typed);
  if (!match) return null;

  let hours: number;
  let minutes: number;
  let meridiem: string | undefined;
  if (match[1] !== undefined) {
    hours = Number(match[1]);
    minutes = Number(match[2]);
    meridiem = match[3];
  } else {
    // Bare digits: the last two are minutes once there are more than two.
    const digits = match[4];
    hours = Number(digits.length > 2 ? digits.slice(0, -2) : digits);
    minutes = digits.length > 2 ? Number(digits.slice(-2)) : 0;
    meridiem = match[5];
  }

  if (minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === 'p' ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Draw "HH:mm" as "hh:mm AM"; anything else is shown as it is. */
export function formatTime(value: string): string {
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) return value;
  const hours = Number(match[1]);
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour12).padStart(2, '0')}:${match[2]} ${hours < 12 ? 'AM' : 'PM'}`;
}
